/**
 * The painting's sky inside the painter's horizontal field (STYLE §4.2).
 *
 * Knots run across the painter window in true azimuth relative to the painter yaw; each knot
 * holds four elevation bands, the dome elevations of painting rows 106-140, 72-106, 38-72 and
 * 4-38 (centres 13.7, 34.8, 55.9, 77.0 deg under the 90 deg x band-fraction mapping).
 *
 * WINDOW_COVERAGE is the STYLE §4.2 cloud mask of docs/reference/cornelis-anthonisz-1538.jpg
 * (sky pixels more than 9.77 L* darker than the clear-sky median of their row) per knot and band,
 * sky-coloured pixels only (a* < -3, L* < 72) so the coat of arms does not count, smoothed once
 * along the knots with a (1/4, 1/2, 1/4) kernel. The clear columns are the reference and are 0.
 * WINDOW_DARKNESS is how far the median cloud of a knot sits from the sheet (L* 29.8) toward
 * cloudDark (L* 19.2): the darkest bank at the right edge of the painting.
 *
 * WINDOW_THRESHOLDS and OUTSIDE_THRESHOLD are noise thresholds calibrated on the TypeScript
 * mirror (skyCalibration.test.ts with AMS_SKY_CALIBRATE=1) so that the same cloud mask applied to
 * the rendered sky reproduces WINDOW_COVERAGE in every cell and 0.555 outside the window. The fit
 * leaves out the azimuth warp that frays the fronts: the warp couples neighbouring knots and makes
 * a per-cell fit oscillate, while by itself it only blurs the layout by about a knot.
 */
export type BandValues = readonly [number, number, number, number];

export const WINDOW_KNOT_COUNT = 32;
export const WINDOW_KNOT_START_DEG = -16;
export const WINDOW_KNOT_STEP_DEG = 32 / 31;
export const WINDOW_BAND_START_DEG = 13.7;
export const WINDOW_BAND_STEP_DEG = 21.1;
/** Painting rows of the four bands, bottom (near the horizon) to top. */
export const WINDOW_BAND_ROWS: readonly (readonly [number, number])[] = [
  [106, 140],
  [72, 106],
  [38, 72],
  [4, 38],
];

export const WINDOW_COVERAGE: readonly BandValues[] = [
  [0, 0, 0, 0],
  [0, 0, 0, 0],
  [0, 0, 0, 0],
  [0, 0, 0, 0],
  [0, 0, 0, 0],
  [0, 0, 0, 0],
  [0, 0, 0, 0],
  [0, 0, 0, 0],
  [0, 0, 0, 0],
  [0.013, 0.005, 0.002, 0.044],
  [0.085, 0.049, 0.021, 0.166],
  [0.253, 0.138, 0.103, 0.341],
  [0.408, 0.273, 0.294, 0.502],
  [0.484, 0.432, 0.494, 0.618],
  [0.6, 0.519, 0.615, 0.641],
  [0.77, 0.612, 0.751, 0.644],
  [0.9, 0.795, 0.896, 0.772],
  [0.972, 0.93, 0.964, 0.933],
  [0.998, 0.981, 0.989, 0.99],
  [1, 0.999, 1, 0.993],
  [1, 1, 1, 0.985],
  [1, 1, 1, 0.968],
  [1, 1, 1, 0.968],
  [1, 1, 1, 0.961],
  [1, 1, 0.996, 0.928],
  [1, 1, 0.942, 0.903],
  [1, 1, 0.849, 0.864],
  [1, 1, 0.854, 0.815],
  [1, 1, 0.945, 0.834],
  [1, 1, 0.995, 0.908],
  [1, 1, 1, 0.969],
  [1, 1, 1, 0.994],
];

export const WINDOW_DARKNESS: readonly number[] = [
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0.031, 0, 0, 0, 0.022, 0.182,
  0.424, 0.932, 1, 1,
];

export const WINDOW_THRESHOLDS: readonly BandValues[] = [
  [0.9, 0.9, 0.9, 0.9],
  [0.9, 0.9, 0.9, 0.9],
  [0.9, 0.9, 0.9, 0.9],
  [0.9, 0.9, 0.9, 0.9],
  [0.9, 0.9, 0.9, 0.9],
  [0.9, 0.9, 0.9, 0.9],
  [0.9, 0.9, 0.9, 0.9],
  [0.9, 0.9, 0.9, 0.9],
  [0.9, 0.9, 0.9, 0.9],
  [0.521, 0.6134, 0.6189, 0.5404],
  [0.5068, 0.6097, 0.6117, 0.51],
  [0.5002, 0.611, 0.589, 0.5045],
  [0.4677, 0.5674, 0.5613, 0.5087],
  [0.4682, 0.5469, 0.5537, 0.4944],
  [0.4704, 0.5324, 0.5426, 0.4976],
  [0.4674, 0.5276, 0.5346, 0.5021],
  [0.461, 0.4987, 0.5253, 0.4865],
  [0.4494, 0.4391, 0.5127, 0.4682],
  [0.4457, 0.4433, 0.51, 0.4845],
  [0.2, 0.2, 0.2, 0.4979],
  [0.2, 0.2, 0.2, 0.4993],
  [0.2, 0.2, 0.2, 0.5003],
  [0.2, 0.2, 0.2, 0.5002],
  [0.2, 0.2, 0.2, 0.5005],
  [0.2, 0.2, 0.4928, 0.4982],
  [0.2, 0.2, 0.5025, 0.4904],
  [0.2, 0.2, 0.5055, 0.4904],
  [0.2, 0.2, 0.5023, 0.5079],
  [0.2, 0.2, 0.5043, 0.5067],
  [0.2, 0.2, 0.5061, 0.5066],
  [0.2, 0.2, 0.2, 0.5085],
  [0.2, 0.2, 0.2, 0.5042],
];

export const OUTSIDE_THRESHOLD = 0.482;
